import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const TransactionReviewTable = ({ transactions, onTransactionClick, onBulkAction }) => {
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [filters, setFilters] = useState({
    status: 'all',
    category: 'all',
    dateRange: 'all'
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified': return 'bg-success/10 text-success border-success/20';
      case 'pending': return 'bg-warning/10 text-warning border-warning/20';
      case 'flagged': return 'bg-error/10 text-error border-error/20';
      default: return 'bg-muted/10 text-muted-foreground border-border';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified': return 'CheckCircle';
      case 'pending': return 'Clock';
      case 'flagged': return 'AlertTriangle';
      default: return 'Circle';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'sales': return 'TrendingUp';
      case 'purchases': return 'ShoppingCart';
      case 'expenses': return 'CreditCard';
      default: return 'FileText';
    }
  };

  const sortedAndFilteredTransactions = useMemo(() => {
    let filtered = [...transactions];

    // Apply filters
    if (filters?.status !== 'all') {
      filtered = filtered?.filter(t => t?.status === filters?.status);
    }
    if (filters?.category !== 'all') {
      filtered = filtered?.filter(t => t?.category === filters?.category);
    }

    // Apply sorting
    filtered?.sort((a, b) => {
      if (sortConfig?.key === 'date') {
        const dateA = new Date(a?.date);
        const dateB = new Date(b?.date);
        return sortConfig?.direction === 'asc' ? dateA - dateB : dateB - dateA;
      }
      if (sortConfig?.key === 'netAmount') {
        return sortConfig?.direction === 'asc' 
          ? a?.netAmount - b?.netAmount 
          : b?.netAmount - a?.netAmount;
      }
      if (sortConfig?.key === 'vatAmount') {
        return sortConfig?.direction === 'asc' 
          ? a?.vatAmount - b?.vatAmount 
          : b?.vatAmount - a?.vatAmount;
      }
      return 0;
    });

    return filtered;
  }, [transactions, filters, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig?.key === key && prevConfig?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedTransactions(sortedAndFilteredTransactions?.map(t => t?.id));
    } else {
      setSelectedTransactions([]);
    }
  };

  const handleSelectTransaction = (transactionId, checked) => {
    if (checked) {
      setSelectedTransactions(prev => [...prev, transactionId]);
    } else {
      setSelectedTransactions(prev => prev?.filter(id => id !== transactionId));
    }
  };

  const handleBulkAction = (action) => {
    onBulkAction(action, selectedTransactions);
    setSelectedTransactions([]);
  };

  const handleExportTransactions = () => {
    const csvData = [
      ['Date', 'Reference', 'Description', 'Category', 'Net Amount', 'VAT Rate', 'VAT Amount', 'Status'],
      ...sortedAndFilteredTransactions?.map(t => [
        new Date(t?.date)?.toLocaleDateString(),
        t?.reference,
        t?.description,
        t?.category,
        t?.netAmount,
        `${t?.taxRate}%`,
        t?.vatAmount,
        t?.status
      ])
    ];
    
    const csvContent = csvData?.map(row => row?.join(','))?.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'transactions-review.csv';
    a?.click();
    URL.revokeObjectURL(url);
  };

  const allSelected = sortedAndFilteredTransactions?.length > 0 && 
    selectedTransactions?.length === sortedAndFilteredTransactions?.length;
  const someSelected = selectedTransactions?.length > 0;

  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Transaction Review</h3>
          {someSelected && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">
                {selectedTransactions?.length} selected
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkAction('verify')}
                iconName="CheckCircle"
                iconPosition="left"
              >
                Mark Verified
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkAction('flag')}
                iconName="AlertTriangle"
                iconPosition="left"
              >
                Flag for Review
              </Button>
            </div>
          )}
        </div>
        
        {/* Filters */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <label className="text-sm text-muted-foreground">Status:</label>
              <select
                value={filters?.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e?.target?.value }))}
                className="text-sm border border-border rounded px-2 py-1 bg-background text-foreground"
              >
                <option value="all">All</option>
                <option value="verified">Verified</option>
                <option value="pending">Pending</option>
                <option value="flagged">Flagged</option>
              </select>
            </div>
            <div className="flex items-center space-x-2">
              <label className="text-sm text-muted-foreground">Category:</label>
              <select
                value={filters?.category}
                onChange={(e) => setFilters(prev => ({ ...prev, category: e?.target?.value }))}
                className="text-sm border border-border rounded px-2 py-1 bg-background text-foreground"
              >
                <option value="all">All</option>
                <option value="sales">Sales</option>
                <option value="purchases">Purchases</option>
                <option value="expenses">Expenses</option>
              </select>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportTransactions}
            iconName="Download"
            iconPosition="left"
          >
            Export CSV
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/20">
            <tr>
              <th className="text-left p-4 font-medium text-foreground">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected && !allSelected}
                  onCheckedChange={handleSelectAll}
                />
              </th>
              <th 
                className="text-left p-4 font-medium text-foreground cursor-pointer hover:text-primary transition-colors"
                onClick={() => handleSort('date')}
              >
                <div className="flex items-center space-x-1">
                  <span>Date</span>
                  <Icon 
                    name={sortConfig?.key === 'date' && sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                    size={14} 
                  />
                </div>
              </th>
              <th className="text-left p-4 font-medium text-foreground">Reference</th>
              <th className="text-left p-4 font-medium text-foreground">Description</th>
              <th className="text-left p-4 font-medium text-foreground">Category</th>
              <th 
                className="text-right p-4 font-medium text-foreground cursor-pointer hover:text-primary transition-colors"
                onClick={() => handleSort('netAmount')}
              >
                <div className="flex items-center justify-end space-x-1">
                  <span>Net Amount</span>
                  <Icon 
                    name={sortConfig?.key === 'netAmount' && sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                    size={14} 
                  />
                </div>
              </th>
              <th 
                className="text-right p-4 font-medium text-foreground cursor-pointer hover:text-primary transition-colors"
                onClick={() => handleSort('vatAmount')}
              >
                <div className="flex items-center justify-end space-x-1">
                  <span>VAT Amount</span>
                  <Icon 
                    name={sortConfig?.key === 'vatAmount' && sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                    size={14} 
                  />
                </div>
              </th>
              <th className="text-center p-4 font-medium text-foreground">Status</th>
              <th className="text-center p-4 font-medium text-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedAndFilteredTransactions?.map((transaction) => (
              <tr 
                key={transaction?.id}
                className="border-b border-border hover:bg-muted/10 transition-colors"
              >
                <td className="p-4">
                  <Checkbox
                    checked={selectedTransactions?.includes(transaction?.id)}
                    onCheckedChange={(checked) => handleSelectTransaction(transaction?.id, checked)}
                  />
                </td>
                <td className="p-4 text-sm text-foreground">
                  {new Date(transaction?.date)?.toLocaleDateString()}
                </td>
                <td className="p-4 text-sm font-medium text-foreground">
                  {transaction?.reference}
                </td>
                <td className="p-4 text-sm text-foreground max-w-xs truncate">
                  {transaction?.description}
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-2">
                    <Icon name={getCategoryIcon(transaction?.category)} size={14} className="text-muted-foreground" />
                    <span className="text-sm text-foreground capitalize">{transaction?.category}</span>
                  </div>
                </td>
                <td className="p-4 text-sm text-right font-medium text-foreground">
                  ${transaction?.netAmount?.toLocaleString()}
                </td>
                <td className="p-4 text-sm text-right font-medium text-foreground">
                  ${transaction?.vatAmount?.toLocaleString()}
                  {transaction?.taxRate > 0 && (
                    <div className="text-xs text-muted-foreground">
                      ({transaction?.taxRate}%)
                    </div>
                  )}
                </td>
                <td className="p-4 text-center">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(transaction?.status)}`}>
                    <Icon name={getStatusIcon(transaction?.status)} size={10} className="mr-1" />
                    {transaction?.status?.charAt(0)?.toUpperCase() + transaction?.status?.slice(1)}
                  </span>
                </td>
                <td className="p-4 text-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onTransactionClick(transaction)}
                    iconName="Eye"
                    iconPosition="left"
                  >
                    View
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {sortedAndFilteredTransactions?.length === 0 && (
        <div className="p-8 text-center">
          <Icon name="Search" size={48} className="mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No transactions match your current filters.</p>
        </div>
      )}
    </div>
  );
};

export default TransactionReviewTable;